<script setup lang="ts">
/**
 * GridAudienceIcons
 *
 * Lightweight grid showing target audiences/industries.
 * Minimal design: just icon + label, no descriptions.
 * Used for "Built for..." or "Who we serve" sections.
 */

import type { GridAudienceIconsProps } from "~~/shared/types";

const props = withDefaults(defineProps<GridAudienceIconsProps>(), {
  columns: 3,
  sectionBg: "neutral",
});

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

const gridColsClass = computed(() => {
  switch (props.columns) {
    case 2:
      return "grid-cols-2 sm:grid-cols-2";
    case 4:
      return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
    case 3:
    default:
      return "grid-cols-2 sm:grid-cols-3";
  }
});
</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-20']">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Header -->
      <div class="mb-10 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ title }}
        </h2>
      </div>

      <!-- Icons Grid -->
      <div :class="['mx-auto grid max-w-4xl gap-6', gridColsClass]">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="group flex flex-col items-center gap-3 rounded-xl bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
        >
          <!-- Icon -->
          <div
            class="flex size-14 items-center justify-center rounded-full bg-mw-accent-50 transition-colors duration-300 group-hover:bg-mw-accent-100"
          >
            <Icon :name="item.icon" size="40" class="text-mw-accent-500" />
          </div>

          <!-- Label -->
          <span class="font-semibold text-slate-700">
            {{ item.label }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
